import React, { useState, useEffect, useRef } from 'react';
import { Box, Paper, Table, TableContainer } from '@mui/material';

import { useDispatch } from 'react-redux';

import { useSnackbar, useTableConfig } from 'hooks';

import CustomSpinner from 'components/shared/CustomSpinner';
import ConfigurationTableModal from 'components/shared/ConfigurationTableModal';
import {
  convertColumns,
  generateTableToCopy,
  getDataColumnsDefault,
} from 'components/shared/CustomTable/helpers';

import type { DataConfigTable, Column } from 'api/shared/models';

import { setFullscreenTable } from 'store/globalApp';

import {
  SAVED_SUCCESSFULLY,
  TABLE_COPIED_SUCCESSFULLY,
  ERROR_COPYING_TABLE,
} from 'constants/messages';

import Pagination from './Pagination';
import CustomTableHead from './CustomTableHead';
import CustomTableBody from './CustomTableBody';
import CustomTableHeader from './CustomTableHeader';
import ConfigurationSortModal from './ConfigurationSortModal';

import type { Props, TableContextType } from './types';

import { defaultConfiguration, defaultPagination } from './constants';

import { styles } from './stylesTable';
import TableContext from './context';

const CustomTable = ({
  columns = [],
  configuration = defaultConfiguration,
  fixedPagination,
  height = 'auto',
  inNotstickyHeader = false,
  isLoading = false,
  othersColumns = [],
  pagination = defaultPagination,
  rows = [],
  showPagination = true,
  size = 'medium',
  variant = 'primary',
  handleChangePage = () => {},
  handleChangeRowsPerPage = () => {},
  handleSortByColumns,
  onCopy,
  ...rest
}: Props) => {
  const { showSnackbarSuccess, showSnackbarError } = useSnackbar();
  const { processGetTableConfiguration, updateTableConfiguration } =
    useTableConfig();
  const dispatch = useDispatch();
  const tableRef = useRef<any>(null);

  const [dense, setDense] = useState(size === 'small');
  const [fullscreen, setFullscreen] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showSortModal, setShowSortModal] = useState(false);
  const [showSpinner, setShowSpinner] = useState(false);

  const [dataColumns, setDataColumns] = useState<DataConfigTable | undefined>();

  const tempColumns = dataColumns?.columns || [];

  const handleChangeDense = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newDense = event.target.checked;
    setDense(newDense);
    if (dataColumns) {
      const newDataColumns = { ...dataColumns, dense: newDense };
      setDataColumns(newDataColumns);
      updateTableConfiguration(newDataColumns);
    }
  };

  const toggleFullscreen = () => setFullscreen((prev) => !prev);

  const handleCloseModal = () => setShowModal(false);
  const handleCloseSortModal = () => setShowSortModal(false);
  const handleOpenConfigModal = () => setShowModal(true);
  const handleOpenSortModal = () => setShowSortModal(true);

  const processUpdateTableConfiguration = async (
    newDataColumns: DataConfigTable,
    showSnackbar = true,
  ) => {
    setShowSpinner(true);
    setDataColumns(newDataColumns);
    await updateTableConfiguration(newDataColumns);
    if (showSnackbar) {
      showSnackbarSuccess(SAVED_SUCCESSFULLY);
    }
    setShowSpinner(false);
  };

  const updateTableConfigurationSimple = async (
    newDataColumns: DataConfigTable,
  ) => {
    setDataColumns(newDataColumns);
    await updateTableConfiguration(newDataColumns);
  };

  const handleUpdateColumns = (newColumns: Column[]) => {
    if (dataColumns) {
      const newDataColumns = {
        ...dataColumns,
        columns: convertColumns(newColumns),
      };
      processUpdateTableConfiguration(newDataColumns);
    }
    handleCloseModal();
  };

  const handleUpdateSortColumns = (newColumns: Column[]) => {
    if (dataColumns) {
      const newDataColumns = { ...dataColumns, sortColumns: newColumns };
      processUpdateTableConfiguration(newDataColumns);
    }
    handleSortByColumns && handleSortByColumns(newColumns);
    handleCloseSortModal();
  };

  const handleUpdateSortColumnByApiField = (apiField: string) => {
    if (!dataColumns) return;

    const newSortColumns = dataColumns?.sortColumns.map((column) => {
      if (column.apiField === apiField) {
        const isAsc = column.direction === 'asc';
        return { ...column, direction: isAsc ? 'desc' : 'asc' };
      }
      return column;
    });

    updateTableConfigurationSimple({
      ...dataColumns,
      sortColumns: newSortColumns || [],
    });
    handleSortByColumns && handleSortByColumns(newSortColumns);
  };

  const handleColumnCollapsed = (column: Column) => {
    if (!dataColumns) return;

    const newColumns = dataColumns.columns.map((columnItem) => {
      if (columnItem.apiField === column.apiField) {
        return {
          ...columnItem,
          collapsed: !column.collapsed,
        };
      }

      return columnItem;
    });
    setDataColumns({ ...dataColumns, columns: newColumns });
  };

  const getTableSx = () => {
    switch (variant) {
      case 'primary':
        return styles.tablePrimary;
      case 'secondary':
        return styles.tableSecondary;
      default:
        return undefined;
    }
  };

  const escFunction = (event: any) => {
    if (event.key === 'Escape') {
      setFullscreen(false);
    }
  };

  useEffect(() => {
    dispatch(setFullscreenTable(fullscreen));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fullscreen]);

  const loadData = async () => {
    if (configuration.showColumnsDefault) {
      setDataColumns(getDataColumnsDefault(columns));
      return;
    }

    if (configuration.showTableHeader && configuration.typeOfTable) {
      const resp = await processGetTableConfiguration(
        configuration.typeOfTable,
        columns,
        configuration.configVersion || 0,
      );

      setDataColumns({ ...resp, columns: convertColumns(resp.columns) });
      if (configuration.showConfigurationSize) {
        setDense(resp.dense || false);
      }
      return;
    }

    setDataColumns(getDataColumnsDefault(columns));
  };

  const handleCopy = () => {
    if (onCopy) {
      const visibleColumns = [...othersColumns, ...tempColumns];
      onCopy(rows, visibleColumns);
    } else {
      try {
        generateTableToCopy(tableRef);
      } catch {
        showSnackbarError(ERROR_COPYING_TABLE);
      }
    }
    showSnackbarSuccess(TABLE_COPIED_SUCCESSFULLY);
  };

  useEffect(() => {
    loadData();
    document.addEventListener('keydown', escFunction, false);
    return () => {
      document.removeEventListener('keydown', escFunction, false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columns]);

  if (showSpinner) {
    return <CustomSpinner />;
  }

  const value: TableContextType = {
    ...rest,
    rows,
    columns: [...othersColumns, ...tempColumns],
    sortColumns: dataColumns?.sortColumns || [],
    isLoading,
    configuration,
    dense,
    fullscreen,
    variant,
    toggleFullscreen,
    handleChangeDense,
    handleOpenConfigModal,
    handleOpenSortModal,
    handleColumnCollapsed,
    handleUpdateSortColumnByApiField,
    onCopy: configuration.showConfigurationCopy ? handleCopy : undefined,
  };

  return (
    <>
      <Box
        sx={
          !inNotstickyHeader
            ? fullscreen
              ? styles.fullscreen
              : styles.headerStatic
            : undefined
        }
      >
        <TableContext.Provider value={value}>
          <Paper>
            <CustomTableHeader />
            <TableContainer
              style={{ height: fullscreen ? 'calc(100vh - 90px)' : height }}
            >
              <Table
                ref={tableRef}
                stickyHeader
                size={dense ? 'small' : 'medium'}
                sx={getTableSx()}
              >
                <CustomTableHead />
                <CustomTableBody rows={rows} center={height !== 'auto'} />
              </Table>
            </TableContainer>
            {showPagination && !fixedPagination ? (
              <Pagination
                fixed={fixedPagination}
                count={pagination.count || 0}
                page={pagination.page}
                rowsPerPage={pagination.pageSize}
                handleChangePage={handleChangePage}
                handleChangeRowsPerPage={handleChangeRowsPerPage}
              />
            ) : null}
          </Paper>
        </TableContext.Provider>
      </Box>
      {showPagination && fixedPagination ? (
        <Pagination
          fixed={fixedPagination}
          count={pagination.count || 0}
          page={pagination.page}
          rowsPerPage={pagination.pageSize}
          handleChangePage={handleChangePage}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
        />
      ) : null}

      {showModal && dataColumns ? (
        <ConfigurationTableModal
          open={showModal}
          handleClose={handleCloseModal}
          tableName={configuration.tableName || ''}
          handleUpdateColumns={handleUpdateColumns}
          dataColumns={dataColumns}
        />
      ) : null}

      {showSortModal && dataColumns ? (
        <ConfigurationSortModal
          open={showSortModal}
          dataColumns={dataColumns}
          handleClose={handleCloseSortModal}
          handleUpdateSortColumns={handleUpdateSortColumns}
        />
      ) : null}
    </>
  );
};

export default CustomTable;
